package com.islamicmedia.shared.platform

/**
 * App-wide analytics facade — the single place event and screen names are defined.
 *
 * Why this exists: the app is one Activity (Android) / one hosting view controller (iOS) with
 * every "screen" living in Compose state, so Firebase's *automatic* screen reporting could only
 * ever see the container and filed everything under `MainActivity`, `ComposeHostingViewController`,
 * `ComposeLayersViewController`, … Automatic reporting is now switched off (AndroidManifest
 * `google_analytics_automatic_screen_reporting_enabled=false`, Info.plist
 * `FirebaseAutomaticScreenReportingEnabled=NO`) and [screen] logs product-shaped names instead.
 *
 * Web is a deliberate no-op: [AnalyticsTracker] on jsMain does nothing, so every call below
 * compiles and silently drops on the Kobweb site.
 */
object Analytics {
    private val tracker = AnalyticsTracker()

    /** Last screen reported, so a recomposition that re-emits the same screen doesn't double-count. */
    private var lastScreen: String? = null

    /**
     * Report the screen the user is looking at. Emits GA4's `screen_view` with a human-readable
     * `screen_name`; `screen_class` is set to the same value because our "classes" (MainScreen,
     * ProfileScreen) carry no product meaning.
     */
    fun screen(name: String) {
        if (name == lastScreen) return
        lastScreen = name
        emitScreen(name)
    }

    /**
     * Re-report the screen the user is already on. Called when the app returns to the foreground:
     * a resume starts a new GA4 session but changes no Compose state, so without this the session
     * would record activity with no screen at all.
     */
    fun reportCurrentScreenAgain() {
        lastScreen?.let { emitScreen(it) }
    }

    private fun emitScreen(name: String) {
        tracker.logEvent("screen_view", mapOf("screen_name" to name, "screen_class" to name))
    }

    fun logEvent(name: String, params: Map<String, Any> = emptyMap()) {
        tracker.logEvent(name, if (params.isEmpty()) params else params.mapValues { sanitize(it.value) })
    }

    /**
     * Two Firebase quirks handled in one place:
     *  - String values longer than 100 chars are silently DROPPED, which would quietly lose long
     *    book/show titles. Truncate so the event still carries a usable value.
     *  - Booleans arrive as 1/0, so a report breakdown reads "quick_check: 1" instead of
     *    "quick_check: true". Send them as text — these are things we segment BY, never sum.
     */
    private fun sanitize(value: Any): Any = when {
        value is Boolean -> value.toString()
        value is String && value.length > 100 -> value.take(97) + "..."
        else -> value
    }
}

/**
 * Human-readable screen names, exactly as they appear in the Firebase "Views by Page title and
 * screen class" report. Written in title case on purpose — these are read by people, not code.
 */
object Screen {
    const val HOME = "Home"
    const val SEARCHING = "Searching"
    const val DISAMBIGUATION = "Disambiguation"
    const val ANALYZING = "Analyzing"
    const val ANALYSIS_DETAIL = "Analysis Detail"
    const val YOUR_ANALYSES = "Your Analyses"
    const val ALTERNATIVES = "Alternatives"
    const val PAYWALL = "Paywall"
    /** The full-screen auth gate (App.kt). Distinct from [SIGN_UP_FROM_PAYWALL], which is the
     *  inline form offered after dismissing the paywall — they're separate funnels. */
    const val AUTH = "Sign In / Sign Up"
    const val SIGN_UP_FROM_PAYWALL = "Sign Up (from Paywall)"
    const val ACCESS_DENIED = "Access Denied"
    /** The avatar menu card (it replaced the avatar's dropdown). */
    const val MENU = "Menu"
    const val PROFILE = "Profile"
    const val SETTINGS = "Settings"
    const val ACCOUNT = "Account"
    const val ACTIVITIES = "Activities"
    const val ABOUT = "About"
    const val BARCODE_SCANNER = "Barcode Scanner"
    const val PHOTO_CANDIDATES = "Photo Candidates"
    const val HELP_FEEDBACK = "Help & Feedback"
    /** The member's own referral code, share link and counts (from the avatar menu). */
    const val REFER_A_FRIEND = "Refer a Friend"
    const val GET_CREDITS = "Get Credits"
    const val PLAN_SWITCHER = "Plan Switcher"
    const val GAME_FITRAH_RUNNER = "Game - Stay on the Fitrah"
    const val DHIKR_COUNTER = "Dhikr Counter"
}

/**
 * Event names. GA4 rules: ≤40 chars, snake_case, must start with a letter. Names already in the
 * Firebase reports are kept verbatim so historical data stays comparable — do not rename them
 * without accepting a break in the report timeline.
 */
object Event {
    // ── Search & analysis ─────────────────────────────────────────────────────────────────────
    const val SEARCH = "search"                                  // existing
    const val VIEW_SEARCH_RESULTS = "view_search_results"        // existing
    const val SELECT_SEARCH_RESULT = "select_search_result"      // existing
    const val SELECT_AUTOCOMPLETE = "select_autocomplete"        // existing
    const val SELECT_RECENT_SEARCH = "select_recent_search"
    const val START_ANALYSIS = "start_analysis"                  // existing
    const val COMPLETE_ANALYSIS = "complete_analysis"            // existing
    const val CANCEL_ANALYSIS = "cancel_analysis"                // existing
    const val ANALYSIS_FAILED = "analysis_failed"                // existing
    /** We asked the OS to show its store review card (mobile only; every 5th completed analysis,
     *  see ReviewPromptRule). The OS decides whether it actually appears. Param "analysis_count". */
    const val REVIEW_PROMPT_REQUESTED = "review_prompt_requested"
    /** Fair-use daily limit hit. [Param.SOURCE] is "preflight" (stopped before the search, from
     *  the entitlement) or "server" (the /api/analyze `daily_cap` rejection). */
    const val DAILY_LIMIT_REACHED = "daily_limit_reached"
    const val RETRY_CONCERN_FAILED = "retry_concern_failed"      // existing
    const val OPEN_CACHED_RESULT = "open_cached_result"          // existing
    const val OPEN_ANALYSIS_DETAIL = "open_analysis_detail"
    const val TOGGLE_QUICK_CHECK = "toggle_quick_check"
    const val RUN_FULL_ANALYSIS = "run_full_analysis"
    const val CHANGE_SEARCH_CRITERIA = "change_search_criteria"

    // ── "Your Analyses" list ──────────────────────────────────────────────────────────────────
    const val PIN_ANALYSIS = "pin_analysis"
    const val HIDE_ANALYSIS = "hide_analysis"
    const val FILTER_ANALYSES = "filter_analyses"
    const val DELETE_ANALYSIS = "delete_analysis"

    // ── Scanning & photo search ───────────────────────────────────────────────────────────────
    const val ISBN_SCANNED = "isbn_scanned"                      // existing
    const val ISBN_CONFIRMED = "isbn_confirmed"                  // existing
    const val PHOTO_SEARCH = "photo_search"
    const val SELECT_PHOTO_CANDIDATE = "select_photo_candidate"

    // ── Recommendations ───────────────────────────────────────────────────────────────────────
    const val ALTERNATIVES_SHOWN = "alternatives_shown"          // existing
    // alternatives_searching is gone with the background discovery job — no longer emitted.
    const val ALTERNATIVES_EMPTY = "alternatives_empty"          // existing
    const val ALTERNATIVES_SELECTED = "alternatives_selected"    // existing
    const val OPEN_ALTERNATIVES_SCREEN = "open_alternatives_screen"

    // ── Community ─────────────────────────────────────────────────────────────────────────────
    const val POST_COMMENT = "post_comment"
    const val EDIT_COMMENT = "edit_comment"
    const val DELETE_COMMENT = "delete_comment"
    const val REACT_TO_ANALYSIS = "react_to_analysis"
    const val FLAG_ANALYSIS = "flag_analysis"                    // existing
    const val SUBMIT_FEEDBACK = "submit_feedback"                // existing

    // ── Activities ────────────────────────────────────────────────────────────────────────────
    const val OPEN_ACTIVITY = "open_activity"
    const val PLAY_GAME = "play_game"
    const val GAME_OVER = "game_over"

    // ── Account & billing ─────────────────────────────────────────────────────────────────────
    const val LOGIN = "login"                                    // GA4 recommended name
    const val SIGN_UP = "sign_up"                                // GA4 recommended name
    const val PAYWALL_SHOWN = "paywall_shown"
    const val PAYWALL_DISMISSED = "paywall_dismissed"
    // Voucher redemption from the paywall. VOUCHER_FAILED carries a `reason` param
    // (not_found / disabled / expired / already_redeemed / already_subscribed / rate_limited),
    // which is what tells you whether a campaign's code is wrong, spent, or being brute-forced.
    const val VOUCHER_OPENED = "voucher_opened"
    const val VOUCHER_REDEEMED = "voucher_redeemed"
    const val VOUCHER_FAILED = "voucher_failed"
    const val BEGIN_CHECKOUT = "begin_checkout"                  // GA4 recommended name
    const val CHANGE_PLAN = "change_plan"

    // ── Referrals ─────────────────────────────────────────────────────────────────────────────
    // The referrer's side: copying and sharing their link from the Refer a Friend screen.
    // REFERRAL_LINK_SHARED carries [Param.METHOD] (how it was shared).
    const val REFERRAL_CODE_COPIED = "referral_code_copied"
    const val REFERRAL_LINK_SHARED = "referral_link_shared"
    // The friend's side: the "Have a referral code?" dialog on the paywall, and a code captured
    // from a /r/ link being attached after sign-in. ENTERED/APPLIED/FAILED carry
    // [Param.REFERRAL_SOURCE] ("typed_paywall" or "link"); FAILED also carries [Param.REASON]
    // (not_found / self / not_new / already_attached / referrer_ineligible / rate_limited / error).
    // APPLIED goes through [Funnel] so the web, where Firebase is a no-op, is counted too.
    const val REFERRAL_CODE_OPENED = "referral_code_opened"
    const val REFERRAL_CODE_ENTERED = "referral_code_entered"
    const val REFERRAL_CODE_APPLIED = "referral_code_applied"
    const val REFERRAL_CODE_FAILED = "referral_code_failed"
    // The friend's side, entered AFTER subscribing: the Free Gift screen's "Got a code from a
    // friend?" (POST /api/referral/redeem), only shown while GET /api/referral/me says can_redeem.
    // REDEEM_FAILED carries [Param.REASON] (not_found / self / not_new / too_late / not_subscribed /
    // already_referred / referrer_ineligible / rate_limited / error). REDEEMED goes through
    // [Funnel], same reason as REFERRAL_CODE_APPLIED.
    const val REFERRAL_REDEEM_OPENED = "referral_redeem_opened"
    const val REFERRAL_REDEEM_ENTERED = "referral_redeem_entered"
    const val REFERRAL_REDEEMED = "referral_redeemed"
    const val REFERRAL_REDEEM_FAILED = "referral_redeem_failed"
    // The one-time "Did a friend send you a code?" popup on the home screen (ReferralCopy.
    // REDEEM_PROMPT_*) — shown to a fresh subscriber a FRESH (not cached) GET /api/referral/me
    // says can_redeem for, at most once ever per account+backend. ENTER also opens the redeem
    // dialog (REFERRAL_REDEEM_OPENED logs separately when that dialog itself opens).
    const val REFERRAL_REDEEM_PROMPT_SHOWN = "referral_redeem_prompt_shown"
    const val REFERRAL_REDEEM_PROMPT_ENTER = "referral_redeem_prompt_enter"
    const val REFERRAL_REDEEM_PROMPT_DISMISSED = "referral_redeem_prompt_dismissed"

    // ── Registration funnel ───────────────────────────────────────────────────────────────────
    // Reported through [Funnel] (admin + Firebase), not [Analytics] alone — these are the steps
    // the /admin funnel measures drop-off across. See [FunnelStep] for the canonical order.
    const val APP_OPENED = "app_opened"
    const val AUTH_SHOWN = "auth_shown"
    /** Left the auth screen still signed out — the sign-up abandon exit. */
    const val AUTH_ABANDONED = "auth_abandoned"
    /** Paywall step 1 → step 2 ("Let's get started, bismillah!"). */
    const val PAYWALL_CONTINUE = "paywall_continue"
    /** Paywall step 2 rendered (the special-offer / plan screen). */
    const val PAYWALL_PLAN_SHOWN = "paywall_plan_shown"
    /** Back arrow on paywall step 2 — returned to the intro instead of choosing a plan. */
    const val PAYWALL_BACK = "paywall_back"
    /** Checkout confirmed (entitlement observed active after returning from Stripe). */
    const val SUBSCRIBED = "subscribed"
    const val OPEN_BILLING_PORTAL = "open_billing_portal"

    // ── Cancellation flow ─────────────────────────────────────────────────────────────────────
    // The in-app exit: survey → up to three save offers → cancel. Grouped by the `cancel_` prefix
    // so the whole funnel sorts together in GA4. CANCEL_OFFER_ACCEPTED/DECLINED carry an `offer`
    // param (discount_50 / pause_1m / free_1m) — which offer saves people is the only number here
    // that changes what we build next.
    const val CANCEL_FLOW_STARTED = "cancel_flow_started"
    const val CANCEL_OFFER_ACCEPTED = "cancel_offer_accepted"
    const val CANCEL_OFFER_DECLINED = "cancel_offer_declined"
    const val CANCEL_CONFIRMED = "cancel_confirmed"
    const val CLICK_JOIN_PROGRAM = "click_join_program"          // existing
    const val VIEW_HISTORY = "view_history"                      // existing
}

/** Parameter keys, so a typo can't silently split one dimension into two in the reports. */
object Param {
    const val TITLE = "title"
    const val MEDIA_TYPE = "media_type"
    const val SOURCE = "source"
    const val METHOD = "method"
    const val QUICK_CHECK = "quick_check"
    const val ENABLED = "enabled"
    const val SCORE = "score"
    /** Separate from [SCORE] on purpose: a single "score" metric would sum analysis verdicts
     *  (0–10) together with game points in any report not filtered by event name. */
    const val GAME_SCORE = "game_score"
    const val GAME = "game"
    const val ACTIVITY = "activity"
    const val REACTION = "reaction"
    const val FILTER = "filter"
    /** NOT "value" — that name is reserved by GA4 for a numeric monetary amount paired with
     *  `currency`, so a text value sent under it can be dropped or mistyped. */
    const val FILTER_VALUE = "filter_value"
    const val PLAN = "plan"

    /** Survey answer key from `CancelReason`. */
    const val REASON = "reason"

    /** Retention offer key from `RetentionOffer`. */
    const val OFFER = "offer"

    /** Where a referral code came from: "typed_paywall" (the dialog) or "link" (a /r/ link). */
    const val REFERRAL_SOURCE = "referral_source"
}
